import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Notification,
  NotificationCategoryLabel,
  NotificationPriority,
} from '@dataTypes/notification.types'
import {
  getNotificationCategory,
  navigateToNotificationTarget,
} from '@utils/notification.util'
import {
  NOTIFICATION_FILTER_CATEGORIES,
  NOTIFICATION_FILTER_STATUS,
  NOTIFICATION_FILTER_LABELS,
  FILTER_SHOW_ALL,
} from '@constants/Notifications'
import {
  fetchNotifications,
  markNotificationReadThunk,
  markNotificationUnreadThunk,
} from '@store/actions/notification.action'
interface UseNotificationsReturn {
  activeTab: string
  searchQuery: string
  selectedDate: string
  filters: Record<string, string[]>
  selectedFilters: Record<string, string[]>
  filterLabels: Record<string, string>
  criticalNotifications: Notification[]
  urgentNotifications: Notification[]
  lowNotifications: Notification[]
  criticalCount: number
  urgentCount: number
  lowCount: number
  filteredNotifications: Notification[]
  readNotifications: Set<string>
  handleChangeTab: (tab: string) => void
  handleSearchChange: (value: string) => void
  handleDateChange: (dateString: string) => void
  handleFilterChange: (filters: Record<string, string[]>) => void
  handleFilterReset: () => void
  handleMarkAsRead: (id: string) => void
  handleAction: (id: string) => void
}

const parseNotificationDate = (dateString: string): moment.Moment => {
  const parsedWithFormat = moment(dateString, 'MMMM D, YYYY', true)
  if (parsedWithFormat.isValid()) {
    return parsedWithFormat
  }

  const autoParsed = moment(dateString)
  if (autoParsed.isValid()) {
    return autoParsed
  }

  return moment.invalid()
}

const useNotifications = (): UseNotificationsReturn => {
  const [activeTab, setActiveTab] = useState<string>('month')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format('MMMM D, YYYY'),
  )
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({
    Category: [FILTER_SHOW_ALL],
  })
  // Local fallback set used only when items lack server-side is_read (e.g. mock data)
  const [readNotifications, setReadNotifications] = useState<Set<string>>(
    new Set(),
  )
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const reduxNotifications = useAppSelector((s) => s.notification.items)

  // Compute date range for the active tab/selected day so the server returns just what we need.
  const { dateFrom, dateTo } = useMemo(() => {
    const selectedMoment = moment(selectedDate, 'MMMM D, YYYY', true).isValid()
      ? moment(selectedDate, 'MMMM D, YYYY')
      : moment(selectedDate)
    const today = moment().startOf('day')
    const isCustomDate =
      selectedMoment.isValid() && !selectedMoment.isSame(today, 'day')

    if (isCustomDate) {
      return {
        dateFrom: selectedMoment.startOf('day').toISOString(),
        dateTo: selectedMoment.endOf('day').toISOString(),
      }
    }
    if (activeTab === 'today') {
      return {
        dateFrom: today.toISOString(),
        dateTo: moment().endOf('day').toISOString(),
      }
    }
    if (activeTab === 'yesterday') {
      const yesterday = moment().subtract(1, 'day').startOf('day')
      return {
        dateFrom: yesterday.toISOString(),
        dateTo: yesterday.endOf('day').toISOString(),
      }
    }
    return {
      dateFrom: moment().startOf('month').toISOString(),
      dateTo: moment().endOf('month').toISOString(),
    }
  }, [activeTab, selectedDate])

  useEffect(() => {
    void dispatch(
      fetchNotifications({
        date_from: dateFrom,
        date_to: dateTo,
      }),
    )
  }, [dispatch, dateFrom, dateTo])

  // Always use the real (server-hydrated) notifications.
  const allNotifications: Notification[] = reduxNotifications

  const filters = useMemo(
    () => ({
      Category: [...NOTIFICATION_FILTER_CATEGORIES],
      Status: [...NOTIFICATION_FILTER_STATUS],
    }),
    [],
  )

  const filterLabels = useMemo(() => ({ ...NOTIFICATION_FILTER_LABELS }), [])

  const handleChangeTab = (tab: string): void => {
    setActiveTab(tab)
    setSelectedDate(moment().format('MMMM D, YYYY'))
  }

  const handleSearchChange = (value: string): void => {
    setSearchQuery(value)
  }

  const handleDateChange = (dateString: string): void => {
    setSelectedDate(dateString)
  }

  const handleFilterChange = (
    filterSelection: Record<string, string[]>,
  ): void => {
    setSelectedFilters(filterSelection)
  }

  const handleFilterReset = (): void => {
    setSelectedFilters({ Category: [FILTER_SHOW_ALL] })
  }

  const isNotificationRead = useCallback(
    (notification: Notification): boolean => {
      if (typeof notification.is_read === 'boolean') {
        return notification.is_read
      }
      const id = notification.notification_id ?? notification.id
      return readNotifications.has(id)
    },
    [readNotifications],
  )

  const handleMarkAsRead = (id: string): void => {
    const notif = allNotifications.find(
      (n) => n.notification_id === id || n.id === id,
    )
    const serverId = notif?.notification_id

    // Real notification: persist via thunk; the reducer flips is_read for us.
    if (serverId) {
      const currentlyRead = isNotificationRead(notif as Notification)
      void dispatch(
        currentlyRead
          ? markNotificationUnreadThunk(serverId)
          : markNotificationReadThunk(serverId),
      )
      return
    }

    // Mock-data fallback: toggle the local set.
    setReadNotifications((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleAction = (id: string): void => {
    const notif = allNotifications.find(
      (n) => n.notification_id === id || n.id === id,
    )
    if (!notif) return
    navigateToNotificationTarget(navigate, notif)
  }

  const filteredNotifications = useMemo(() => {
    let filtered = allNotifications

    const selectedMoment = moment(selectedDate, 'MMMM D, YYYY', true).isValid()
      ? moment(selectedDate, 'MMMM D, YYYY')
      : moment(selectedDate)
    const todayMoment = moment().startOf('day')
    const isCustomDateSelected =
      selectedMoment.isValid() && !selectedMoment.isSame(todayMoment, 'day')

    if (isCustomDateSelected) {
      const selectedDateStart = selectedMoment.startOf('day')
      filtered = filtered.filter((notif) => {
        const notifDate = parseNotificationDate(notif.date).startOf('day')
        return notifDate.isValid() && notifDate.isSame(selectedDateStart, 'day')
      })
    } else {
      if (activeTab === 'today') {
        const today = moment().startOf('day')
        filtered = filtered.filter((notif) => {
          const notifDate = parseNotificationDate(notif.date).startOf('day')
          return notifDate.isValid() && notifDate.isSame(today, 'day')
        })
      } else if (activeTab === 'yesterday') {
        const yesterday = moment().subtract(1, 'day').startOf('day')
        filtered = filtered.filter((notif) => {
          const notifDate = parseNotificationDate(notif.date).startOf('day')
          return notifDate.isValid() && notifDate.isSame(yesterday, 'day')
        })
      } else if (activeTab === 'month') {
        const currentMonth = moment().startOf('month')
        filtered = filtered.filter((notif) => {
          const notifDate = parseNotificationDate(notif.date)
          return notifDate.isValid() && notifDate.isSame(currentMonth, 'month')
        })
      }
    }

    // Hide dismissed notifications from the list view.
    filtered = filtered.filter((notif) => !notif.dismissed)

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (notif) =>
          notif.title.toLowerCase().includes(query) ||
          notif.message.toLowerCase().includes(query),
      )
    }

    if (selectedFilters.Category && selectedFilters.Category.length > 0) {
      const selectedLabels = selectedFilters.Category.filter(
        (cat) => cat !== FILTER_SHOW_ALL,
      )
      if (selectedLabels.length > 0) {
        filtered = filtered.filter((notif) => {
          // Prefer the backend-supplied category enum; fall back to the legacy
          // keyword heuristic for mock items that don't carry one. Compare on
          // display labels so the filter selections (which are labels) match
          // either path consistently.
          const category = notif.category ?? getNotificationCategory(notif)
          const label = NotificationCategoryLabel[category]
          return label ? selectedLabels.includes(label) : false
        })
      }
    }

    if (selectedFilters.Status && selectedFilters.Status.length > 0) {
      filtered = filtered.filter((notif) => {
        const isRead = isNotificationRead(notif)
        const hasRead = selectedFilters.Status.includes('Read')
        const hasUnread = selectedFilters.Status.includes('Unread')

        if (hasRead && hasUnread) {
          return true
        }
        if (hasRead) {
          return isRead
        }
        if (hasUnread) {
          return !isRead
        }
        return true
      })
    }

    return filtered
  }, [
    allNotifications,
    activeTab,
    selectedDate,
    searchQuery,
    selectedFilters,
    isNotificationRead,
  ])

  const criticalNotifications = useMemo(
    () =>
      filteredNotifications.filter(
        (n) => n.priority === NotificationPriority.CRITICAL,
      ),
    [filteredNotifications],
  )

  const urgentNotifications = useMemo(
    () =>
      filteredNotifications.filter(
        (n) => n.priority === NotificationPriority.URGENT,
      ),
    [filteredNotifications],
  )

  const lowNotifications = useMemo(
    () =>
      filteredNotifications.filter(
        (n) => n.priority === NotificationPriority.LOW,
      ),
    [filteredNotifications],
  )

  const criticalCount = criticalNotifications.length
  const urgentCount = urgentNotifications.length
  const lowCount = lowNotifications.length

  // For consumers that still read `readNotifications` (e.g. styling),
  // expose a derived set that combines server-side is_read with the local mock fallback.
  const derivedReadSet = useMemo(() => {
    const set = new Set(readNotifications)
    for (const n of allNotifications) {
      if (n.is_read) {
        set.add(n.notification_id ?? n.id)
      }
    }
    return set
  }, [allNotifications, readNotifications])

  return {
    activeTab,
    searchQuery,
    selectedDate,
    filters,
    selectedFilters,
    filterLabels,
    criticalNotifications,
    urgentNotifications,
    lowNotifications,
    criticalCount,
    urgentCount,
    lowCount,
    filteredNotifications,
    readNotifications: derivedReadSet,
    handleChangeTab,
    handleSearchChange,
    handleDateChange,
    handleFilterChange,
    handleFilterReset,
    handleMarkAsRead,
    handleAction,
  }
}

export default useNotifications
